import { Table, Button, Space, Input, Select, Card, Row, Col, Statistic } from 'antd';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchShipStationInventory,
  fetchShipStationWarehouses,
  fetchProductCategories,
  fetchShipStationInventoryStats,
  fetchProductWarehouseLocations,
} from '../api/products.api';
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { useStores } from '../hooks/useStores';

function getStorePrefix(storeName: string): string {
  return storeName.replace(/\s*-\s*fulfillment\s*$/i, '').trim();
}

const baseFilters = { page: 1, pageSize: 20 };

export default function InventoryManagement() {
  const { user } = useAuth();
  const stores = useStores();

  const { data: allWarehouses = [] } = useQuery({
    queryKey: ['ss-warehouses'],
    queryFn: fetchShipStationWarehouses,
  });

  const { data: allCategories = [] } = useQuery({
    queryKey: ['product-categories'],
    queryFn: fetchProductCategories,
  });

  const assignedStoreIds = (user?.assignedStores ?? []).map(String);
  const isUnrestricted =
    user?.type === 'superadmin' ||
    (user?.type === 'admin' && assignedStoreIds.length === 0) ||
    assignedStoreIds.length === 0;

  const assignedPrefixes = isUnrestricted
    ? []
    : stores
        .filter(s => assignedStoreIds.includes(String(s.storeId)))
        .map(s => getStorePrefix(s.storeName));

  // warehouses are already filtered server-side
  const availableWarehouses: { inventory_warehouse_id: string; name: string }[] = allWarehouses;

  const availableCategories: { categoryId: number; name: string }[] =
    isUnrestricted
      ? allCategories
      : (allCategories as any[]).filter((cat: any) =>
          assignedPrefixes.some(p => cat.name?.toLowerCase().startsWith(p.toLowerCase()))
        );

  const { data: stats } = useQuery({
    queryKey: ['ss-inventory-stats'],
    queryFn: fetchShipStationInventoryStats,
  });

  const [filters, setFilters] = useState<any>(baseFilters);
  const [local, setLocal] = useState({ sku: '', name: '', warehouseName: undefined as string | undefined, warehouseLocation: undefined as string | undefined, categoryId: undefined as number | undefined, stockStatus: undefined as string | undefined });

  const { data: allWarehouseLocations = [] } = useQuery({
    queryKey: ['product-warehouse-locations'],
    queryFn: fetchProductWarehouseLocations,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['ss-inventory', filters],
    queryFn: () => fetchShipStationInventory(filters),
    keepPreviousData: true,
  } as any);

  function apply() {
    setFilters((prev: any) => {
      const f: any = { page: 1, pageSize: prev.pageSize };
      if (local.sku) f.sku = local.sku;
      if (local.name) f.name = local.name;
      if (local.warehouseName) f.warehouseName = local.warehouseName;
      if (local.warehouseLocation) f.warehouseLocation = local.warehouseLocation;
      if (local.categoryId !== undefined) f.categoryId = local.categoryId;
      if (local.stockStatus) f.stockStatus = local.stockStatus;
      if (prev.sortBy) { f.sortBy = prev.sortBy; f.sortOrder = prev.sortOrder; }
      return f;
    });
  }

  function clear() {
    setLocal({ sku: '', name: '', warehouseName: undefined, warehouseLocation: undefined, categoryId: undefined, stockStatus: undefined });
    setFilters(baseFilters);
  }

  function handleTableChange(pagination: any, _: any, sorter: any) {
    setFilters((prev: any) => {
      const f: any = { page: pagination.current, pageSize: pagination.pageSize };
      if (prev.sku) f.sku = prev.sku;
      if (prev.name) f.name = prev.name;
      if (prev.warehouseName) f.warehouseName = prev.warehouseName;
      if (prev.warehouseLocation) f.warehouseLocation = prev.warehouseLocation;
      if (prev.categoryId !== undefined) f.categoryId = prev.categoryId;
      if (prev.stockStatus) f.stockStatus = prev.stockStatus;
      if (sorter?.field && sorter?.order) {
        f.sortBy = sorter.field;
        f.sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
      }
      return f;
    });
  }

  const sortedColumn = filters.sortBy;
  const sortedOrder = filters.sortOrder === 'asc' ? 'ascend' : filters.sortOrder === 'desc' ? 'descend' : undefined;

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card
            hoverable={!!filters.stockStatus}
            style={{ cursor: filters.stockStatus ? 'pointer' : 'default' }}
            onClick={() => {
              setLocal(l => ({ ...l, stockStatus: undefined }));
              setFilters((prev: any) => { const f = { ...prev, page: 1 }; delete f.stockStatus; return f; });
            }}
          >
            <Statistic title="Total Products" value={stats?.totalProducts ?? '-'} prefix={<AppstoreOutlined />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={5}>
          <Card>
            <Statistic title="Total On Hand" value={stats?.totalOnHand ?? '-'} prefix={<DatabaseOutlined />} valueStyle={{ color: '#1677ff' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={5}>
          <Card
            hoverable
            style={{ cursor: 'pointer', outline: filters.stockStatus === 'in_stock' ? '2px solid #3f8600' : undefined }}
            onClick={() => {
              const val = 'in_stock';
              setLocal(l => ({ ...l, stockStatus: val }));
              setFilters((prev: any) => { const f = { ...prev, page: 1, stockStatus: val }; return f; });
            }}
          >
            <Statistic title="In Stock" value={stats?.inStock ?? '-'} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={5}>
          <Card
            hoverable
            style={{ cursor: 'pointer', outline: filters.stockStatus === 'low_stock' ? '2px solid #d48806' : undefined }}
            onClick={() => {
              const val = 'low_stock';
              setLocal(l => ({ ...l, stockStatus: val }));
              setFilters((prev: any) => { const f = { ...prev, page: 1, stockStatus: val }; return f; });
            }}
          >
            <Statistic title="Low Stock (≤10)" value={stats?.lowStock ?? '-'} prefix={<WarningOutlined />} valueStyle={{ color: '#d48806' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={5}>
          <Card
            hoverable
            style={{ cursor: 'pointer', outline: filters.stockStatus === 'out_of_stock' ? '2px solid #cf1322' : undefined }}
            onClick={() => {
              const val = 'out_of_stock';
              setLocal(l => ({ ...l, stockStatus: val }));
              setFilters((prev: any) => { const f = { ...prev, page: 1, stockStatus: val }; return f; });
            }}
          >
            <Statistic title="Out of Stock" value={stats?.outOfStock ?? '-'} prefix={<CloseCircleOutlined />} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
      </Row>

      <Space style={{ marginBottom: 12 }} wrap>
        <Input
          placeholder="SKU"
          value={local.sku}
          onChange={e => setLocal({ ...local, sku: e.target.value })}
          style={{ width: 140 }}
        />
        <Input
          placeholder="Product name"
          value={local.name}
          onChange={e => setLocal({ ...local, name: e.target.value })}
          style={{ width: 180 }}
        />
        <Select
          placeholder="All Categories"
          allowClear
          showSearch
          style={{ width: 200 }}
          value={local.categoryId}
          onChange={val => setLocal({ ...local, categoryId: val })}
          filterOption={(input, option) =>
            (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
          }
          options={availableCategories.map((c: any) => ({
            label: c.name,
            value: c.categoryId,
          }))}
        />
        <Select
          placeholder="All Warehouses"
          allowClear
          showSearch
          style={{ width: 200 }}
          value={local.warehouseName}
          onChange={val => setLocal({ ...local, warehouseName: val })}
          filterOption={(input, option) =>
            (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
          }
          options={[
            { label: 'Not Assigned', value: '__not_assigned__' },
            ...availableWarehouses.map((wh: any) => ({
              label: wh.name,
              value: wh.name,
            })),
          ]}
        />
        <Select
          placeholder="All Locations"
          allowClear
          showSearch
          style={{ width: 200 }}
          value={local.warehouseLocation}
          onChange={val => setLocal({ ...local, warehouseLocation: val })}
          filterOption={(input, option) =>
            (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
          }
          options={(allWarehouseLocations as string[]).map(loc => ({
            label: loc,
            value: loc,
          }))}
        />
        <Select
          placeholder="All Stock Status"
          allowClear
          style={{ width: 180 }}
          value={filters.stockStatus}
          onChange={val => {
            setLocal(l => ({ ...l, stockStatus: val }));
            setFilters((prev: any) => {
              const f: any = { page: 1, pageSize: prev.pageSize };
              if (prev.sku) f.sku = prev.sku;
              if (prev.name) f.name = prev.name;
              if (prev.warehouseName) f.warehouseName = prev.warehouseName;
              if (prev.warehouseLocation) f.warehouseLocation = prev.warehouseLocation;
              if (prev.categoryId !== undefined) f.categoryId = prev.categoryId;
              if (val) f.stockStatus = val;
              if (prev.sortBy) { f.sortBy = prev.sortBy; f.sortOrder = prev.sortOrder; }
              return f;
            });
          }}
          options={[
            { label: 'In Stock', value: 'in_stock' },
            { label: 'Low Stock (≤10)', value: 'low_stock' },
            { label: 'Out of Stock', value: 'out_of_stock' },
          ]}
        />
        <Button type="primary" onClick={apply}>Search</Button>
        <Button onClick={clear}>Clear</Button>
      </Space>

      <Table
        loading={isLoading}
        rowKey={(() => {
          const seen = new Map<string, number>();
          return (r: any) => {
            const base = `${r.sku}-${r.warehouse_name ?? ''}-${r.category_name ?? ''}`;
            const count = seen.get(base) ?? 0;
            seen.set(base, count + 1);
            return count === 0 ? base : `${base}-${count}`;
          };
        })()}
        dataSource={(data as any)?.data}
        scroll={{ x: true }}
        pagination={{
          current: filters.page,
          pageSize: filters.pageSize,
          total: (data as any)?.total,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total.toLocaleString()} records`,
        }}
        onChange={handleTableChange}
        columns={[
          { title: 'SKU', dataIndex: 'sku', width: 160 },
          { title: 'Product Name', dataIndex: 'name', sorter: true, sortOrder: sortedColumn === 'name' ? sortedOrder : undefined },
          { title: 'Product Category', dataIndex: 'category_name', width: 180, render: (v: any) => v || '-' },
          { title: 'Warehouse', dataIndex: 'warehouse_name', width: 180, render: (v: any) => v || 'Not Assigned' },
          { title: 'Location', dataIndex: 'warehouseLocation', width: 180, render: (v: any) => v || '-' },
          { title: 'On Hand', dataIndex: 'on_hand', width: 110, sorter: true, sortOrder: sortedColumn === 'on_hand' ? sortedOrder : undefined, render: (v: any) => <strong>{v ?? '-'}</strong> },
          { title: 'Available', dataIndex: 'available', width: 110, sorter: true, sortOrder: sortedColumn === 'available' ? sortedOrder : undefined, render: (v: any) => {
            const color = (v == null || v === 0) ? '#cf1322' : v <= 10 ? '#d48806' : '#3f8600';
            return <strong style={{ color }}>{v ?? 0}</strong>;
          }},
          { title: 'Allocated', dataIndex: 'allocated', width: 110, sorter: true, sortOrder: sortedColumn === 'allocated' ? sortedOrder : undefined, render: (v: any) => v ?? '-' },
        ]}
      />
    </div>
  );
}
